import React, { useCallback } from 'react';
import { useGlobalSearchParams, useRouter } from 'expo-router';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { Button, IllusLocation, Modal, ModalDialogue } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useBagCount, useCartHandoff } from '../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const CartHandoffBagWarning = () => {
  const isFeatureFlagEnabled = useFeatureFlag('CELS-5511-cart-handoff-enabled');
  const router = useRouter();
  const { formatMessage } = useIntl();

  // ─── State ─────────────────────────────────────────────────────────────────

  const { orderToken } = useGlobalSearchParams<{ orderToken?: string }>();
  const { bagCount } = useBagCount();
  const shouldShowWarning = isFeatureFlagEnabled && Boolean(orderToken) && bagCount > 0;

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const { handleCartHandoff, isHandingOffCart } = useCartHandoff();

  const continueWithCartHandoff = useCallback(() => {
    if (!orderToken || isHandingOffCart) return;

    void handleCartHandoff(orderToken);
  }, [handleCartHandoff, orderToken, isHandingOffCart]);

  const cancelPendingCartHandoff = useCallback(() => {
    router.setParams({ orderToken: undefined });
  }, [router]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      size="medium"
      imageSrc={IllusLocation}
      visible={shouldShowWarning}
      accessibilityLabel={formatMessage(messages.title)}
      headline={formatMessage(messages.title)}
      body={formatMessage(messages.subtitle)}
      withCloseBtn={!isHandingOffCart}
      onRequestClose={cancelPendingCartHandoff}
    >
      <Modal.Footer withoutTopBorder>
        <Button
          palette="primary"
          size="large"
          accessibilityRole="button"
          aria-label={formatMessage(messages.cta)}
          isLoading={isHandingOffCart}
          onPress={continueWithCartHandoff}
        >
          <FormattedMessage {...messages.cta} />
        </Button>
      </Modal.Footer>
    </ModalDialogue>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'You already have an active bag.',
    description: 'Cart Handoff | Title',
  },
  subtitle: {
    defaultMessage: 'Proceeding with a pre-made order will clear your current bag.',
    description: 'Cart Handoff | Subtitle',
  },
  cta: {
    defaultMessage: 'Clear bag & continue',
    description: 'Cart Handoff | CTA',
  },
  close: {
    defaultMessage: 'Close',
    description: 'Cart Handoff | Close',
  },
});
